import { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, Newspaper, Award, X } from "lucide-react";

const SPLASH_SHOWN_KEY = "diplomexa_splash_shown_at";
// Show the splash once per 12 h per device (prevents popping on every route)
const SPLASH_COOLDOWN_MS = 12 * 60 * 60 * 1000;

/**
 * Diplomexa Welcome Splash — Sprint 2.
 *
 * A 2-second animated greeting that fades in on first dashboard load each day,
 * personalised by role + name, with a role-aware "Today's brief" news card.
 * Dismissable with the X button or Esc; auto-dismiss after 10 s.
 */
export default function WelcomeSplash() {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    const last = parseInt(localStorage.getItem(SPLASH_SHOWN_KEY) || "0", 10);
    if (Date.now() - last < SPLASH_COOLDOWN_MS) return;
    api.get("/welcome/feed").then((r) => {
      setFeed(r.data);
      setOpen(true);
      localStorage.setItem(SPLASH_SHOWN_KEY, String(Date.now()));
      // auto-dismiss after 10s
      const t = setTimeout(() => setOpen(false), 10000);
      return () => clearTimeout(t);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!open || !feed) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0A192F]/85 backdrop-blur-sm animate-fade-in"
      data-testid="welcome-splash"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative w-[min(720px,92vw)] rounded-3xl overflow-hidden bg-gradient-to-br from-[#0A192F] via-[#0F2C50] to-[#102b4e] border border-[#D4AF37]/40 shadow-[0_30px_120px_-20px_rgba(212,175,55,0.4)] animate-splash-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative gold orb glow */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-[#0F4C81]/30 blur-3xl" />

        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center transition-colors"
          data-testid="welcome-splash-close"
          aria-label="Dismiss splash"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative p-10">
          <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Diplomexa
          </p>
          <h2
            className="mt-3 text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight"
            data-testid="welcome-splash-greeting"
          >
            {feed.greeting}
          </h2>
          <p className="mt-2 text-base text-slate-300">
            {feed.user?.name && (
              <>
                Good to see you,{" "}
                <span className="text-[#D4AF37] font-semibold">{feed.user.name.split(" ")[0]}</span>.{" "}
              </>
            )}
            Here&apos;s your <span className="text-white font-medium">{feed.role_label}</span> brief for today.
          </p>

          {feed.mun101_badge && (
            <div
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 px-3 py-1.5 text-xs text-[#D4AF37] font-semibold"
              data-testid="welcome-splash-mun101-badge"
            >
              <Award className="h-3.5 w-3.5" />
              MUN 101 Certified · earned {new Date(feed.mun101_badge.earned_at).toLocaleDateString()}
            </div>
          )}

          <div className="mt-7 rounded-2xl bg-white/5 border border-white/10 p-5">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Newspaper className="h-3 w-3" /> {feed.news?.label || "Today's brief"}
            </p>
            <div className="mt-3 space-y-3" data-testid="welcome-splash-news">
              {(feed.news?.items || []).slice(0, 3).map((n, i) => (
                <a
                  key={i}
                  href={n.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                  data-testid={`welcome-splash-news-${i}`}
                >
                  <p className="text-sm font-medium text-white leading-snug group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                    {n.title.replace(/ - [^-]+$/, "")}
                  </p>
                  {n.source && (
                    <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                      {n.source}
                    </p>
                  )}
                </a>
              ))}
              {(feed.news?.items?.length || 0) === 0 && (
                <p className="text-xs text-slate-400 italic">
                  News feed unavailable right now — we&apos;ll refresh it shortly.
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-5 py-2.5 rounded-full bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A192F] font-bold text-sm transition-colors"
              data-testid="welcome-splash-enter"
            >
              Enter dashboard
            </button>
            <p className="text-[11px] text-slate-400">
              Tap anywhere outside or press Esc to dismiss
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes splash-in {
          0%   { opacity: 0; transform: translateY(24px) scale(0.95); }
          60%  { opacity: 1; transform: translateY(-4px) scale(1.02); }
          100% { opacity: 1; transform: translateY(0)    scale(1); }
        }
        .animate-fade-in   { animation: fade-in   0.35s ease-out both; }
        .animate-splash-in { animation: splash-in 0.65s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
